import React from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { type ChartTheme } from '../Services/ChartThemeManager'

export interface ChartCloseButtonProps {
  /** Chart identifier for the close action */
  chartId: string
  
  /** Visual theme for the button */
  theme?: ChartTheme
  
  /** Callback when close button is clicked */
  onClose?: (chartId: string) => void
  
  /** Whether to show confirmation dialog */
  requireConfirmation?: boolean
  
  /** Custom confirmation message */
  confirmationMessage?: string
  
  /** Whether the button is disabled */
  disabled?: boolean
  
  /** Additional CSS styles */
  style?: React.CSSProperties
  
  /** Custom CSS class name */
  className?: string
  
  /** Accessibility label */
  'aria-label'?: string
}

/**
 * Close button component for chart containers
 * Provides visual feedback and optional confirmation for chart removal
 */
export function ChartCloseButton({
  chartId,
  theme = 'dark',
  onClose,
  requireConfirmation = false,
  confirmationMessage = 'Are you sure you want to remove this chart?',
  disabled = false,
  style,
  className,
  'aria-label': ariaLabel = 'Close chart'
}: ChartCloseButtonProps) {
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    if (disabled || !onClose) return

    if (requireConfirmation) {
      if (window.confirm(confirmationMessage)) {
        onClose(chartId)
      }
    } else {
      onClose(chartId)
    }
  }

  return (
    <Tooltip title="Close Chart" placement="top">
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={disabled}
        className={className}
        aria-label={ariaLabel}
        data-testid="chart-close-button"
        data-chart-id={chartId}
        sx={{
          width: '32px',
          height: '32px',
          border: '1px solid',
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          backgroundColor: 'transparent',
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(244, 67, 54, 0.1)', // Light red background on hover
            borderColor: 'rgba(244, 67, 54, 0.5)', // Red border on hover
            color: 'rgba(244, 67, 54, 0.8)' // Red text on hover
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
          },
          ...style
        }}
      >
        {/* X icon */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 6L6 18" />
          <path d="M6 6l12 12" />
        </svg>
      </IconButton>
    </Tooltip>
  )
}

export default ChartCloseButton 